import { Component, OnInit, ViewChild, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';
import { Observable } from 'rxjs';
import { OrderConstants } from 'src/app/core/constants/order-constants';
import { OrderStatusConstants } from 'src/app/core/constants/order-status-constants';
import { OrderFacade } from 'src/app/store/order/facade';
import { OrderModel, DictionaryItemDTO, EmployeeDTO, PropertyValueDTO, PropertyDTO, StatusDTO } from 'src/app/store/order/model';
import { ServiceTypeFacade } from 'src/app/store/service-type/facade';
import { UserFacade } from 'src/app/store/user/facade';
import { OrderDynamicAttrsComponent } from 'src/app/shared/components/order/order-dynamic-attrs/order-dynamic-attrs.component';
import { ComponentCanDeactivate } from 'src/app/shared/guards/can-deactivate-guard';
import { AbstractOrderUIService } from 'src/app/core/services/abstract/abstract-order-ui-service';
import { AppConstants } from '../../../../core/constants/app-constants';

declare var gtag;

@Component({
  selector: 'app-order',
  templateUrl: './order.component.html',
  styleUrls: ['./order.component.scss'],
  standalone: false,
})
export class OrderComponent implements OnInit, OnDestroy, ComponentCanDeactivate {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  translate = inject(TranslateService);
  private orderFacade = inject(OrderFacade);
  private userFacade = inject(UserFacade);
  private serviceTypeFacade = inject(ServiceTypeFacade);
  private orderUIService = inject(AbstractOrderUIService);

  public isUkLanguage: boolean;
  public externalRate: number;
  public externalNextStatusId: number;

  order: OrderModel;
  sourceOrder: OrderModel;

  public serviceId: number;

  @ViewChild('dynamicAttrs', { read: OrderDynamicAttrsComponent, static: false }) dynamicAttrs;

  confirmActionMessage: string = '';
  dataWasChangeMessage: string = '';
  createOrderByNumberMessage: string = '';

  creatingProcess: boolean = false;

  pages = [
    { id: 1, caption: 'BASIC' },
    { id: 2, caption: 'ACTIONS' },
    { id: 3, caption: 'FILES' },
  ];

  ngOnInit(): void {
    this.isUkLanguage = this.translate.currentLang == AppConstants.LANGUAGE_UK;

    this.translate.get('CONFITM_ACTION').subscribe(value => {
      this.confirmActionMessage = value;
    });

    this.translate.get('DATA_WAS_CHANGE').subscribe(value => {
      this.dataWasChangeMessage = value;
    });

    this.translate.get('CREATED_ORDER_BY_NUMBER').subscribe(value => {
      this.createOrderByNumberMessage = value;
    });

    this.route.queryParamMap.subscribe(params => {
      let categoryId = +this.route.snapshot.queryParamMap.get('categoryId');
      let categoryName = this.route.snapshot.queryParamMap.get('categoryName');
      this.serviceId = +this.route.snapshot.queryParamMap.get('serviceId');
      let serviceName = this.route.snapshot.queryParamMap.get('serviceName');
      let serviceTypeId = +this.route.snapshot.queryParamMap.get('serviceTypeId');

      if (this.serviceId > 0) {
        this.setDefaultValues(categoryId, categoryName, this.serviceId, serviceName, serviceTypeId);

        this.orderFacade.loadTemplate(categoryId, this.serviceId);

        this.orderFacade.selected().subscribe(order => {
          if (order != null && this.creatingProcess) {
            notify({ message: this.createOrderByNumberMessage + order.id, width: 600 }, 'success', 5000);
            this.router.navigate(['/orders/order', order.id]);

            gtag('event', 'order_was_created');
          }
        });
      }
    });

    this.route.params.subscribe(params => {
      let orderId = +params['id'];

      this.externalRate = +this.route.snapshot.queryParamMap.get('evaluation');
      this.externalNextStatusId = +this.route.snapshot.queryParamMap.get('nextStatusId');

      if (orderId > 0) {
        this.orderFacade.setSelected(orderId);

        this.orderFacade.selected().subscribe(order => {
          this.order = OrderModel.copy(order);

          if (order && order.service && order.task) {
            this.order.canEditCategory = false;
            this.orderFacade.loadTemplate(order.service.id, order.task.id);
          }

          this.sourceOrder = OrderModel.copy(this.order);
        });
      }
    });
  }

  ngOnDestroy() {
    this.creatingProcess = false;
    this.orderFacade.clearSelected();
  }

  canDeactivate(): boolean | Observable<boolean> | Promise<boolean> {
    if (!this.creatingProcess && this.isChanged()) {
      return confirm(this.dataWasChangeMessage, this.confirmActionMessage);
    } else {
      return true;
    }
  }

  isChangedOrder(): boolean {
    return JSON.stringify(this.sourceOrder) !== JSON.stringify(this.order);
  }

  isChanged(): boolean {
    return this.isChangedOrder() || this.order?.addedFiles.length > 0;
  }

  setDefaultValues(categoryId: number, categoryName: string, serviceId: number, serviceName: string, serviceTypeId: number) {
    if (!this.order) {
      this.order = new OrderModel();
    }

    this.order.createDate = new Date();
    this.order.title = serviceName ? serviceName : '???';
    this.order.service = new DictionaryItemDTO(categoryId, categoryName ? categoryName : '-');
    this.order.task = new DictionaryItemDTO(serviceId, serviceName ? serviceName : '???');
    this.order.urgency = new DictionaryItemDTO(OrderConstants.CreatingUrgencyId, '-');

    this.order.canEditCategory = !(+serviceTypeId > 0);

    this.order.resource = new DictionaryItemDTO(OrderConstants.Resource.UserWebSD.Id, OrderConstants.Resource.UserWebSD.Value);

    //this.serviceTypeFacade.getById(this.order.canEditCategory ? 1 : serviceTypeId).subscribe(x => {
    //  this.order.category = x ? new DictionaryItemDTO(x.id, x.nameRu) : new DictionaryItemDTO(null, null);
    //});

    this.orderFacade.template().subscribe(template => {
      if (template) {
        this.serviceTypeFacade.getById(template.typeId).subscribe(st => {
          if (st) {
            this.order.category = new DictionaryItemDTO(st.id, this.isUkLanguage ? st.nameUa : st.nameRu);
          }
        });
      }
    });

    this.translate.get('CREATING').subscribe(x => {
      this.order.status = new StatusDTO(OrderStatusConstants.OrderStatuses.Creating, x);
    });

    this.userFacade.selected().subscribe(x => {
      if (x) {
        this.order.employee = new EmployeeDTO(x.id, `${x.lastName} ${x.firstName} ${x.secondName}`);
        this.order.employeeFor = new EmployeeDTO(x.id, `${x.lastName} ${x.firstName} ${x.secondName}`);
        this.orderFacade.setSelectedEmployeeForId(this.order.employeeFor.id);
      }
    });
  }

  onFormSubmit = function (e) {
    if (this.orderUIService.validateFiles(this.order.addedFiles) === true) {
      if (!this.order.id) {
        this.creatingProcess = true;

        this.createProperties();
        this.orderFacade.create(this.order, this.order.addedFiles);
      } else {
        this.orderFacade.save(this.order, this.isChanged(), this.order.addedFiles);
      }
    }

    e.preventDefault();
  };

  createProperties() {
    this.order.properties = [];

    this.dynamicAttrs.properties.forEach(property => {
      let newProp = new PropertyValueDTO();
      newProp.property = new PropertyDTO(property.propertyId);
      newProp.value = property.value;
      this.order.properties.push(newProp);
    });
  }
}
